import { bufferToHexString, typedArrayToBuffer, } from '@iotize/common/byte-converter';
import { KaitaiStreamWriter } from '@iotize/common/byte-stream';
import { Buffer } from 'buffer';
import { debug } from './debug';
const TAG = 'TLV';
export var TLV;
(function (TLV) {
    const decoders = {
        String: function (payload, offset, len) {
            return payload.toString('utf8', offset, offset + len);
        },
        Integer: function (payload, offset, len) {
            return payload.readIntBE(offset, len);
        },
        Float: function (payload, offset, len) {
            switch (len) {
                case 4:
                    return payload.readFloatBE(offset);
                case 8:
                    return payload.readDoubleBE(offset);
                default:
                    throw new Error(`Invalid length for float: ${len}`);
            }
        },
        Boolean: function (payload, offset, len) {
            return payload.readUInt8(offset) ? true : false;
        },
        Opaque: function (payload, offset, len) {
            if (payload.length < offset + len) {
                throw new Error(`Buffer too small. Expecting ${len} bytes but only ${payload.length - offset} avaiable(s). Offset of ${offset}`);
            }
            const buf = Buffer.alloc(len);
            payload.copy(buf, 0, offset, offset + len);
            return buf;
        },
        Time: function (payload, offset, len) {
            const timestamp = payload.readIntBE(offset, len);
            return new Date(timestamp * 1e3);
        },
    };
    let DataType;
    (function (DataType) {
        DataType["String"] = "String";
        DataType["Integer"] = "Integer";
        DataType["Float"] = "Float";
        DataType["Boolean"] = "Boolean";
        DataType["Opaque"] = "Opaque";
        DataType["Time"] = "Time";
        DataType["Array"] = "Array";
    })(DataType = TLV.DataType || (TLV.DataType = {}));
    let IdentifierType;
    (function (IdentifierType) {
        IdentifierType[IdentifierType["OBJECT_INSTANCE"] = 0] = "OBJECT_INSTANCE";
        IdentifierType[IdentifierType["RESOURCE_INSTANCE"] = 1] = "RESOURCE_INSTANCE";
        IdentifierType[IdentifierType["MULTIPLE_RESOURCE"] = 2] = "MULTIPLE_RESOURCE";
        IdentifierType[IdentifierType["RESOURCE_VALUE"] = 3] = "RESOURCE_VALUE";
    })(IdentifierType = TLV.IdentifierType || (TLV.IdentifierType = {}));
    function tlvType(type, id, len) {
        let byte = type << 6;
        if (id > 0xff) {
            byte |= 0x1 << 5;
        }
        if (len < 8) {
            byte |= len;
        }
        else {
            byte |= length(len) << 3;
        }
        return byte;
    }
    TLV.tlvType = tlvType;
    class Parser {
        constructor(schema) {
            this.position = 0;
            this.schema = schema;
            if (this.schema) {
                throw new Error(`Schema are not supported yet`);
            }
        }
        /**
         *
         * @param buf
         * @param offset
         * @param tlv
         */
        readHeader() {
            let type, id, len;
            let header;
            header = this.buffer.readUInt8(this.position);
            this.position += 1;
            type = header >>> 6;
            if (header & 0x20) {
                id = this.buffer.readUInt16BE(this.position);
                this.position += 2;
            }
            else {
                id = this.buffer.readUInt8(this.position);
                this.position += 1;
            }
            len = header & 0x7;
            if (!len) {
                switch ((header & 0x18) >>> 3) {
                    case 1:
                        len = this.buffer.readUInt8(this.position);
                        this.position += 1;
                        break;
                    case 2:
                        len = this.buffer.readUInt16BE(this.position);
                        this.position += 2;
                        break;
                    case 3:
                        len = this.buffer.readUInt16BE(this.position);
                        len = this.buffer.readUInt8(this.position);
                        this.position += 3;
                        break;
                }
            }
            const result = {
                id: id,
                type: type,
                len: len,
            };
            this.debug(`\t-Found header: `, result);
            return result;
        }
        readNextRecord() {
            const header = this.readHeader();
            const key = header.id.toString();
            if (header.type === IdentifierType.RESOURCE_VALUE ||
                header.type === IdentifierType.RESOURCE_INSTANCE) {
                this.debug(`\t-Resource value readRecords with`, header);
                const payload = Buffer.alloc(header.len);
                this.buffer.copy(payload, 0, this.position, this.position + header.len);
                this.position += header.len;
                return {
                    header: header,
                    payload: payload,
                };
            }
            else {
                this.debug(`\t-Multiple resources found! readRecords with key ${key}`);
                const children = this.readAllRecords(this.position + header.len);
                return {
                    header: header,
                    children: children,
                };
            }
        }
        append(header, type) {
            this.debug(`\t-Append ${type} header=`, header);
            if (!(type in decoders)) {
                throw new Error(`Decoder type ${type} not found`);
            }
            const result = decoders[type](this.buffer, this.position, header.len);
            this.debug(`\t-Appending value`, result);
            this.position += header.len;
            return result;
        }
        readAllRecords(endPosition) {
            const result = [];
            this.debug(`Parsing records until position ${endPosition}`);
            while (this.position < endPosition) {
                result.push(this.readNextRecord());
            }
            return result;
        }
        parse(data) {
            this.position = 0;
            this.buffer = Buffer.from(typedArrayToBuffer(data));
            return this.readNextRecord();
        }
        debug(...args) {
            debug(TAG, ...args, `[pos=${this.position}/${this.buffer.length - 1}, remaining buffer=${bufferToHexString(this.buffer.slice(this.position))}]`);
        }
    }
    TLV.Parser = Parser;
    class Serializer {
        serialize(obj, stream) {
            stream = stream || new KaitaiStreamWriter();
            this.writeHeader(stream, obj.header.type, obj.header.id, obj.header.len);
            if (obj.payload) {
                stream.writeBytes(obj.payload);
            }
            if (obj.children) {
                for (const node of obj.children) {
                    this.serialize(node, stream);
                }
            }
            return stream.toBytes;
        }
        writeHeader(stream, idType, id, len) {
            const type = tlvType(idType, id, len);
            /* type (8-bits masked field) */
            stream.writeU(type, 1);
            /* identifier (8-bit or 16-bit UInt) */
            if (type & 0x20) {
                stream.writeU(id, 2);
            }
            else {
                stream.writeU(id, 1);
            }
            /* length (0-24-bit UInt) */
            if (type & 0x18) {
                switch (length(len)) {
                    case 3:
                        stream.writeU((len >>> 0x10) & 0xff, 1);
                    /* falls through */
                    case 2:
                        stream.writeU((len >>> 0x08) & 0xff, 1);
                    /* falls through */
                    case 1:
                        stream.writeU(len & 0xff, 1);
                        break;
                    default:
                        throw new Error('Invalid resource `' + id + '`');
                }
            }
        }
        serializeObj(obj, schema) {
            const buf = Buffer.alloc(16 * 1024 /*1024*/);
            const keys = Object.keys(obj);
            if (schema) {
                schema.validate(obj);
            }
            let len = 0;
            for (let i = 0; i < keys.length; ++i) {
                const key = keys[i];
                // skip resources not defined in schema
                if (!schema || !schema.resources[key]) {
                    continue;
                }
                len = writeRecordWithSchema(obj, buf, len, key);
            }
            return buf.slice(0, len);
        }
    }
    TLV.Serializer = Serializer;
    function writeHeader(buf, offset, idType, id, len) {
        const type = tlvType(idType, id, len);
        /* type (8-bits masked fielsd) */
        buf.writeUInt8(type, offset);
        offset += 1;
        /* identifier (8-bit or 16-bit UInt) */
        if (type & 0x20) {
            buf.writeUInt16BE(id, offset);
            offset += 2;
        }
        else {
            buf.writeUInt8(id, offset);
            offset += 1;
        }
        /* length (0-24-bit UInt) */
        if (type & 0x18) {
            switch (length(len)) {
                case 3:
                    buf.writeUInt8((len >>> 0x10) & 0xff, offset);
                    offset += 1;
                /* falls through */
                case 2:
                    buf.writeUInt8((len >>> 0x08) & 0xff, offset);
                    offset += 1;
                /* falls through */
                case 1:
                    buf.writeUInt8(len & 0xff, offset);
                    offset += 1;
                    break;
                default:
                    throw new Error('Invalid resource `' + id + '`');
            }
        }
        return offset;
    }
    function writeData(buf, offset, len, value, type) {
        // if (type){
        //     switch (type){
        //         case DataType.String:
        //             stream.write();
        //         case DataType.Boolean:
        //             stream.writeB
        //     }
        // }
        const ValueSerializerMap = {
            String: function () {
                buf.write(value, offset, buf.length - offset, 'utf8');
                return (offset += len);
            },
            Integer: function () {
                buf.writeIntBE(value, offset, len);
                return (offset += len);
            },
            Float: function () {
                buf.writeDoubleBE(value, offset);
                return (offset += len);
            },
            Boolean: function () {
                buf.writeInt8(value, offset);
                return (offset += len);
            },
            Opaque: function () {
                value.copy(buf, offset);
                return (offset += len);
            },
            Time: function () {
                // convert date to timestamp in seconds
                const timestamp = (value.getTime() / 1e3) >> 0;
                buf.writeIntBE(timestamp, offset, len);
                return (offset += len);
            },
            Array: function () {
                return offset;
            },
        };
        if (!type) {
            return offset;
        }
        if (type in ValueSerializerMap) {
            return ValueSerializerMap[type]();
        }
        return offset;
    }
    function writeRecordWithSchema(obj, buf, offset, key, schema) {
        if (!schema) {
            return offset; // TODO check this one
        }
        const res = schema.resources[key];
        if (!res) {
            return offset; // TODO check this one
        }
        const val = obj[key];
        let len;
        if (res && Array.isArray(res.type)) {
            const tmp = Buffer.alloc(1024);
            let arrLen = 0;
            val.forEach(function (elem, i) {
                len = length(elem);
                arrLen = writeHeader(tmp, arrLen, IdentifierType.RESOURCE_INSTANCE, i, len);
                arrLen = writeData(tmp, arrLen, len, elem, res.type[0]);
            });
            offset = writeHeader(buf, offset, IdentifierType.MULTIPLE_RESOURCE, parseInt(res.id, 10), arrLen);
            tmp.copy(buf, offset, 0, arrLen);
            offset += arrLen;
        }
        else {
            len = length(val);
            offset = writeHeader(buf, offset, IdentifierType.RESOURCE_VALUE, parseInt(res.id, 10), len);
            offset = writeData(buf, offset, len, val, res.type);
        }
        return offset;
    }
    /*
     * length in bytes of a number
     */
    function byteLength(val) {
        if (val < 2) {
            return 1;
        }
        return Math.ceil((Math.log(val) * Math.LOG2E) / 8); // TODO can be shorter
    }
    function length(value) {
        // integer size: 1, 2, 4 or 8 bytes.
        function size(numberValue) {
            let v = byteLength(numberValue);
            v--;
            v |= v >>> 1;
            v |= v >>> 2;
            v++;
            return v;
        }
        const type = Object.prototype.toString.call(value).slice(8, -1);
        switch (type) {
            case 'Number':
                return value % 1 === 0 ? size(value) : 8;
            case 'String':
            case 'Uint8Array':
            case 'Buffer':
                return value.length;
            case 'Boolean':
                return 1;
            case 'Date':
                return size((value.getTime() / 1e3) >> 0);
            default:
                return 0;
        }
    }
})(TLV || (TLV = {}));
